import { computed, inject, Injectable, Signal } from "@angular/core";
import { IEnrichmentModel } from "@app/shared/models/enrichment.model";
import { isFlatEnrichmentModel } from "@app/shared/mappers/enrichment-detail.adapter";
import { LanguageConfigStore } from "@app/shared/services/language-config.store";
import { TAB_ALIASES } from "../../components/edit/enrichment-edit-package/tab-algebra.util";
import { PublishReadinessService } from "./publish-readiness.service";
import { PublishIssue } from "./types";
import { TabLocaleCoverage, TabReadinessSummary } from "./tab-readiness.types";
import {
  detailSliceForTab,
  TAB_DETAIL_KEYS,
  TABS_WITHOUT_LOCALE_COVERAGE,
} from "./tab-detail-keys";

/** Locale-key regex copied from flat-language-coverage; kept local so we
 *  can walk an arbitrary subtree without exporting internals. */
const LOCALE_KEY = /^[a-z]{2,3}-[A-Za-z]{2,4}$/;

function isLocaleBag(v: unknown): v is Record<string, unknown> {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  return Object.keys(v).some((k) => LOCALE_KEY.test(k));
}

function collectBags(
  node: unknown,
  out: Record<string, unknown>[],
  seen: Set<object>,
): void {
  if (!node || typeof node !== "object") return;
  if (seen.has(node)) return;
  seen.add(node);
  if (isLocaleBag(node)) {
    out.push(node);
    return;
  }
  if (Array.isArray(node)) {
    for (const item of node) collectBags(item, out, seen);
    return;
  }
  for (const val of Object.values(node)) {
    collectBags(val, out, seen);
  }
}

function coverageForSlice(
  slice: unknown,
  locales: readonly string[],
): Record<string, TabLocaleCoverage> {
  const result: Record<string, TabLocaleCoverage> = {};
  for (const l of locales) result[l] = { locale: l, filled: 0, total: 0 };
  if (!slice) return result;

  const bags: Record<string, unknown>[] = [];
  collectBags(slice, bags, new Set());
  for (const bag of bags) {
    for (const l of locales) {
      result[l].total += 1;
      const v = bag[l];
      if (typeof v === "string" && v.trim().length > 0) {
        result[l].filled += 1;
      }
    }
  }
  return result;
}

function normalizeTabId(tabId: string): string {
  return TAB_ALIASES[tabId] ?? tabId;
}

@Injectable({ providedIn: "root" })
export class TabReadinessService {
  private readonly publishReadiness = inject(PublishReadinessService);
  private readonly languageConfig = inject(LanguageConfigStore);

  /** Per-tab summary computed from the publish-readiness pipeline. The
   *  returned Signal recomputes whenever the model or the active locale
   *  set changes.
   *
   *  Unmapped tabs get zero coverage rather than the whole-detail fallback,
   *  which would inflate totals. */
  summary(
    model: Signal<IEnrichmentModel | null>,
  ): Signal<Map<string, TabReadinessSummary>> {
    return computed(() => {
      const m = model();
      const readiness = this.publishReadiness.evaluate(m);
      const locales = this.languageConfig.activeLocales();

      const issuesByTab = new Map<string, PublishIssue[]>();
      for (const section of readiness.sections) {
        for (const issue of section.issues) {
          if (!issue.tabId) continue;
          const tabId = normalizeTabId(issue.tabId);
          const existing = issuesByTab.get(tabId);
          if (existing) {
            existing.push(issue);
          } else {
            issuesByTab.set(tabId, [issue]);
          }
        }
      }

      const detail = isFlatEnrichmentModel(m) ? m.__detail : null;

      // Consider every tab that either has an issue OR can carry language
      // coverage — a tab with only language gaps (no blocker/warning) must
      // still surface in the banner, otherwise the gap list silently vanishes
      // the moment its last must-fix is resolved.
      const coverageTabs = Object.keys(TAB_DETAIL_KEYS).filter(
        (t) => !TABS_WITHOUT_LOCALE_COVERAGE.has(t),
      );
      const candidateTabs = new Set<string>([
        ...issuesByTab.keys(),
        ...coverageTabs,
      ]);

      const result = new Map<string, TabReadinessSummary>();
      for (const tabId of candidateTabs) {
        const issues = issuesByTab.get(tabId) ?? [];
        // Unmapped tabs get zero coverage to avoid inflating totals with
        // the whole-detail fallback that `detailSliceForTab` would return.
        const langCoverage: Record<string, TabLocaleCoverage> =
          tabId in TAB_DETAIL_KEYS && !TABS_WITHOUT_LOCALE_COVERAGE.has(tabId)
            ? coverageForSlice(detailSliceForTab(detail, tabId), locales)
            : Object.fromEntries(
                locales.map((l) => [l, { locale: l, filled: 0, total: 0 }]),
              );

        const hasLangGap = Object.values(langCoverage).some(
          (c) => c.total > 0 && c.filled < c.total,
        );

        // Nothing worth surfacing → leave the tab out so the banner stays
        // hidden (and the stepper falls back to its own status) on clean tabs.
        if (issues.length === 0 && !hasLangGap) continue;

        result.set(tabId, {
          tabId,
          blockerCount: issues.filter((i) => i.severity === "error").length,
          warningCount: issues.filter((i) => i.severity === "warning").length,
          langCoverage,
          issues,
        });
      }
      return result;
    });
  }
}
